import { notifications_16 as notifications16 } from "@trustpilot/icons";
import { useTranslations } from "ilenia";
import React, { useRef } from "react";

import { setJobRole, setSeenNotifications, setSeenWhatsNew } from "../../../api";
import { notificationModals, trackingConst } from "../../../constants";
import { useAnalytics } from "../../../utils/analytics";
import { NavMenuHeader } from "../common/navMenuHeader";
import Menu from "../menu";
import { NavIcon } from "../styled";
import { YourRoleModal } from "./modals/yourRoleModal";
import NotificationList from "./notificationList";
import { StyledCount, StyledSection } from "./styled";
import * as Types from "./types";

const notificationsTypes = {
  YOUR_NOTIFICATIONS: "yourNotifications",
  WHATS_NEW: "whatsNew",
};

const notificationsPanelId = "notifications-panel";
export const ariaLabelHeading = "notifications-heading";

const Notifications: React.FC<Types.Notifications> = ({
  toggled,
  toggle,
  items,
  businessUserId,
  businessUserEmail,
  businessUnit,
}) => {
  const analytics = useAnalytics();
  const [translations] = useTranslations();
  const [notifications, setNotifications] = React.useState(items);
  const [notificationModal, setNotificationModal] = React.useState<string>("");
  const navIconRef = useRef<HTMLButtonElement>();

  React.useEffect(() => {
    setNotifications(items);
  }, [items]);

  const setNotificationRead = React.useCallback(
    async (businessUnitId, id, isProductUpdate, read) => {
      if (read) {
        return;
      }

      const updatedNotifications = notifications.map((n) => {
        if (n.id === id) {
          n.state = "seen";
        }
        return n;
      });

      // 1. call user pref api
      if (isProductUpdate) {
        await setSeenWhatsNew(updatedNotifications, businessUserId);
      } else {
        await setSeenNotifications(updatedNotifications, businessUnitId, businessUserId);
      }

      // 2. set notifications
      setNotifications(updatedNotifications);
    },
    [businessUserId, notifications],
  );

  const onNotificationItemClick = React.useCallback(
    async (id: string, isProductUpdate = false, read: boolean) => {
      const clickedNotification = notifications.find((item) => item.id === id);
      if (clickedNotification.action.type === "custom" && clickedNotification.action.descriptor === "opens-modal") {
        setNotificationModal(id);
      }

      return setNotificationRead(businessUnit.id, id, isProductUpdate, read).then(() => {
        analytics.track(trackingConst.events.notificationsItemClicked, {
          context: trackingConst.contexts.accountSelector,
          name: trackingConst.names.notificationItemClicked,
          id,
        });
      });
    },
    [analytics, businessUnit.id, notifications],
  );

  const handleSubmitRole = React.useCallback(
    (role: string, otherRole: string) => {
      setJobRole(otherRole || role, businessUserId);
      analytics.identify(businessUserEmail, { jobRole: role });
      analytics.track(trackingConst.events.buttonClicked, {
        context: trackingConst.contexts.appshell,
        name: "Notification - Submit job role",
        value: role,
        data: otherRole,
      });
      setNotificationModal("");
    },
    [analytics, businessUserId, businessUserEmail],
  );

  const onCloseNotificationModal = React.useCallback(() => {
    analytics.track(trackingConst.events.buttonClicked, {
      context: trackingConst.contexts.appshell,
      name: "Notification - Close job role modal",
    });
    setNotificationModal("");
  }, [analytics]);

  const yourNotifications = notifications.filter(({ type }) => type !== "productUpdate");
  const yourNotificationsItemsToNotify = yourNotifications.filter((n) => n.state === "new").length;

  const [count, setCount] = React.useState({
    [notificationsTypes.YOUR_NOTIFICATIONS]: 0,
    [notificationsTypes.WHATS_NEW]: 0,
    total: 0,
  });

  React.useEffect(() => {
    const notificationItems = notifications.filter((item) => item.type !== "productUpdate" && item.state === "new");
    const productItems = notifications.filter((item) => item.type === "productUpdate" && item.state === "new");
    setCount({
      [notificationsTypes.YOUR_NOTIFICATIONS]: notificationItems.length,
      [notificationsTypes.WHATS_NEW]: productItems.length,
      total: notificationItems.length + productItems.length,
    });
  }, [notifications]);

  // Called when opening the notification bell.
  const toggleNotificationsPopup = React.useCallback(() => {
    if (!toggled) {
      analytics.track(trackingConst.events.notificationsClicked, {
        unreadCount: notifications.length.toString(),
        notificationItems: notifications.filter((item) => item.type !== "productUpdate").map((item) => item.id),
        productItems: notifications.filter((item) => item.type === "productUpdate").map((item) => item.id),
      });
    }
    toggle();
  }, [toggled, analytics, notifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called when clicking a notification item.
  const yourNotificationsItemClicked = React.useCallback(
    async (notificationId: string, read: boolean) => {
      await onNotificationItemClick(notificationId, false, read);
    },
    [count, yourNotificationsItemsToNotify, onNotificationItemClick], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Track when clicking an URL within a notification - does not mark it as read, since the "notification" wasn't clicked.
  const trackNotificationItemUrlClicked = React.useCallback(
    async (id: string, title: string, target: string) => {
      analytics.track(trackingConst.events.notificationsItemUrlClicked, {
        context: trackingConst.contexts.appshell,
        name: "Notification - click link inside notification",
        id,
        title,
        target,
      });
    },
    [analytics],
  );

  // Called when clicking a What's new notification.
  const whatsNewItemClicked = React.useCallback(
    (notificationId: string, read: boolean) => {
      return onNotificationItemClick(notificationId, true, read);
    },

    [onNotificationItemClick], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onItemClicked = React.useCallback(
    async (evt, id, action, read, type) => {
      evt.preventDefault();

      const setClicked = (id: string) => {
        notifications.map((n) => {
          if (n.id === id) {
            n.state = "clicked";
          }
          return n;
        });
        setNotifications(notifications);
      };

      setClicked(id);

      if (type === "productUpdate") {
        whatsNewItemClicked(id, read);
      } else {
        await yourNotificationsItemClicked(id, false);
      }

      if (action.type === "redirect") {
        if (evt.metaKey || evt.ctrlKey) {
          window.open(action.path);
        } else {
          window.location.href = decodeURIComponent(action.path);
        }
      }

      if (action.type === "external") {
        window.open(action.path, "_blank");
      }

      if (!evt.metaKey && !evt.ctrlKey) {
        toggle();
      }
    },
    [yourNotificationsItemClicked, whatsNewItemClicked],
  );

  const focusIcon = () => {
    if (!toggled) {
      navIconRef.current.focus();
    }
  };

  return (
    <>
      <Menu
        toggled={toggled}
        toggle={toggleNotificationsPopup}
        target={
          <>
            {count?.total ? (
              <StyledCount onClick={focusIcon} aria-hidden>
                {count.total}
              </StyledCount>
            ) : null}
            <NavIcon
              ref={navIconRef}
              icon={notifications16}
              appearance="subtle"
              ariaLabel={`${count?.total ?? 0} ${
                translations["BusinessUserSettings.notifications.header.navigation.yourNotifications"]
              }`}
              aria-expanded={toggled}
              aria-controls={notificationsPanelId}
            />
          </>
        }
        testid="businessUserNotifications"
        id={notificationsPanelId}
      >
        <StyledSection aria-labelledby={ariaLabelHeading}>
          <NavMenuHeader
            ariaLabel={ariaLabelHeading}
            icon={notifications16}
            headingLocoKey="BusinessUserSettings.notifications.header.navigation.yourNotifications"
          />
          <NotificationList
            businessUserId={businessUserId}
            items={notifications}
            onItemClicked={onItemClicked}
            trackNotificationItemUrlClicked={trackNotificationItemUrlClicked}
          />
        </StyledSection>
      </Menu>
      {notificationModal === notificationModals.YOUR_ROLE && (
        <YourRoleModal onClose={onCloseNotificationModal} businessUnit={businessUnit} onSubmit={handleSubmitRole} />
      )}
    </>
  );
};

export default Notifications;
